import React, { useState, useContext, useEffect } from 'react';
import { Spin, Empty, Pagination } from 'antd';
import { useTranslation } from 'react-i18next';

import { AppContext } from 'context/AppContext';
import { useFeed } from 'services/FeedService';
import { Submission } from 'services/types/TaskTypes';
import FeedItem from './FeedItem';
import MasonryLayout from './MasonryLayout';
import '../Feed.css';

interface FeedListProps {
  className?: string;
}

const FeedList: React.FC<FeedListProps> = ({ className }) => {
  const { t } = useTranslation();
  const { authentication } = useContext(AppContext);
  const { authKey } = authentication;
  
  const [page, setPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const pageSize = 20;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const { data: feedData, isLoading } = useFeed(page, authKey);
  const submissions = feedData?.results || [];

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmissionLikeUpdate = (updatedSubmission: Submission) => {
    // You can implement optimistic updates here if needed
    console.log('Submission like updated:', updatedSubmission);
  };

  const handleSubmissionCommentUpdate = (submissionId: string, newComment: any) => {
    // You can implement optimistic updates here if needed
    console.log('Comment added to submission:', submissionId, newComment);
  };

  if (!authKey) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={`feed-list ${className || ''}`}>
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (submissions.length === 0) {
    return (
      <div className={`feed-list ${className || ''}`}>
        <div className="empty-container">
          <Empty 
            description={t('#Feed.no_feed_items')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`feed-list ${className || ''}`}>
      <MasonryLayout 
        columns={isMobile ? 1 : 2} 
        gap={isMobile ? 12 : 16}
        className="feed-masonry-container"
      >
        {submissions.map((item) => (
          <div key={item.id} className="feed-masonry-item">
            <FeedItem
              item={item}
              onLikeUpdate={handleSubmissionLikeUpdate}
              onCommentAdd={(newComment) => handleSubmissionCommentUpdate(item.id, newComment)}
              isInCommentDrawer={false}
              showCommentInput={false}
            />
          </div>
        ))}
      </MasonryLayout>

      {/* Pagination */}
      {feedData?.meta?.pagination && feedData.meta.pagination.pages > 1 && (
        <div className="pagination-container">
          <Pagination
            current={page}
            total={feedData.meta.pagination.total}
            pageSize={pageSize}
            onChange={handlePageChange}
            showSizeChanger={false}
            showQuickJumper={false}
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total}`
            }
            hideOnSinglePage
            simple={false}
          />
        </div>
      )}
    </div>
  );
};

export default FeedList;